"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { formatBRL } from "@/lib/utils"
import { Label } from "@/components/ui/label"
import { CodigoQr } from "@/components/design-new/codigo-qr"
import { ICONE, Icone } from "@/components/design-new/icone"
/* Marca de terceiro não é ícone do sistema: Pix vive em `marcas.tsx`. */
import { MarcaPix } from "@/components/design-new/marcas"
import {
  AssinaturaDaPagina,
  AvisoDaPagina,
  BotaoDaPagina,
  CaixaDaPagina,
  CampoDaPagina,
  CartaoDaPagina,
  GarantiaDoCartaoDaPagina,
  OfertaDaPagina,
  OpcaoDaPagina,
  TextoDaPagina,
} from "@/components/page-builder/pecas-da-pagina"
import { useIsMobile } from "@/lib/hooks/use-mobile"
import { usePagarNoCartao, type PagamentoNoCartao, type RespostaDoCartao } from "@/lib/hooks/use-pagar-no-cartao"
import { ErroDaPaginaDePagar, fraseDaFalha, lerDaApi, mandarParaApi, quemRecebeEscrito } from "@/lib/pagar/api-publica"
import { celularDoPix } from "@/lib/utils/telefone"
import { validadeDoPixEscrita } from "@/lib/utils/tempo"
import { BlockPreview, containerMaxClass, fontClass, pageThemeStyle, radiusValue } from "@/components/page-builder/block-preview"
import { TEMA_DEFAULT } from "@/components/page-builder/block-templates"
import { fotoPerfil } from "@/lib/utils/cdn"
import { copiarTexto } from "@/lib/utils/copiar-texto"
import { mascaraCpf, validarCpf } from "@/lib/utils/cpf"
import { problemaNoNome } from "@/lib/utils/nome"
import type { PageTheme } from "@/lib/services/lead-service"
import leadService from "@/lib/services/lead-service"
import type { OfertaDepois, PaginaDeVendaDados, PrecoOfertado } from "@/lib/seo/pagina-de-venda"
import { CampoDoCupom, FaixaDoCupom, PrecoComCupom, useCupomDaPagina } from "./cupom-da-pagina"
import { parcelasQueCabem, valorDaParcela } from "@/lib/utils/parcelamento"

/*
  ─── A página de venda do produto, no navegador ───

  A parte interativa: o pixel do personal (sempre com `trackSingle`, para o
  evento não vazar para a nossa conta), o quiz, os eventos de funil e o
  checkout. Até 12/10/2026 ela também buscava a página num `useEffect`, e quem
  lê o HTML sem rodar JavaScript (os robôs de busca das IAs, o Screaming Frog)
  recebia só o "carregando". Agora a página chega pronta do servidor
  (`page.tsx`, com os dados de `lib/seo/pagina-de-venda.ts`), e este componente
  não busca nada.
*/

const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br"

interface CompraResult {
  venda_id?: string
  fb_event_id?: string | null
  invoice_url?: string
  pix_qrcode_base64?: string
  pix_payload?: string | null
  /** O split (21/10/2026): a venda nasceu no Pagar.me, sem página de banco. */
  gateway?: "asaas" | "pagarme"
  forma?: "PIX" | "CARTAO"
  /** Até quando o QR do split vale (ISO). */
  vence_em?: string | null
  /** O cartão parcelado do split: quantas vezes e quanto cada uma. */
  parcelas?: number
  valor_parcela?: number
  valor_primeira_parcela?: number
  valor: number
  taxa: number
  valor_liquido: number
  /** As ofertas do pagamento: as que entraram e as que ficaram fora (13/10/2026). */
  ofertas?: {
    junto: Array<{ oferta_id: string; nome: string; valor: number }>
    fora_da_compra: Array<{ oferta_id: string; nome: string | null; motivo: "ja_tem" | "indisponivel" }>
  }
  /** O cupom que deu desconto, e quanto ele tirou do preço (13/10/2026). */
  cupom?: { codigo: string; desconto: number } | null
}

/** O que aconteceu com a oferta marcada que não entrou na compra, numa frase. */
function fraseDaOfertaFora(nome: string, motivo: "ja_tem" | "indisponivel"): string {
  return motivo === "ja_tem"
    ? `"${nome}" saiu da compra: você já tem esse produto.`
    : `"${nome}" saiu da compra: ele não está mais à venda.`
}

function parseTema(raw: PaginaDeVendaDados["tema"]): PageTheme {
  if (!raw) return { ...TEMA_DEFAULT }
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw
    return { ...TEMA_DEFAULT, ...obj }
  } catch {
    return { ...TEMA_DEFAULT }
  }
}

/** Normaliza um valor (param ou host do referrer) para um rótulo de origem amigável. */
function rotuloOrigem(valor: string): string {
  const v = valor.toLowerCase()
  if (v.includes("instagram") || v === "ig") return "Instagram"
  if (v.includes("facebook") || v === "fb" || v === "meta") return "Facebook"
  if (v.includes("whatsapp") || v.includes("wa.me") || v === "wpp" || v === "zap") return "WhatsApp"
  if (v.includes("youtube") || v.includes("youtu.be") || v === "yt") return "YouTube"
  if (v.includes("tiktok")) return "TikTok"
  if (v.includes("google")) return "Google"
  if (v.includes("t.co") || v.includes("twitter") || v === "x" || v.includes("x.com")) return "Twitter/X"
  if (v.includes("linktr") || v.includes("bio")) return "Link na bio"
  if (v.includes("telegram") || v === "tg") return "Telegram"
  if (v.includes("mail") || v.includes("email")) return "E-mail"
  if (v === "direct" || v === "direto" || v === "") return "Direto"
  return valor.charAt(0).toUpperCase() + valor.slice(1)
}

/** Lê um cookie pelo nome (client-side). */
function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"))
  return m ? decodeURIComponent(m[1]) : undefined
}

/** Coleta fbp (_fbp) e fbc (_fbc ou derivado do fbclid) pra atribuição/CAPI. */
function coletarFbParams(): { fbp?: string; fbc?: string } {
  if (typeof window === "undefined") return {}
  const fbclid = new URLSearchParams(window.location.search).get("fbclid")
  const fbp = getCookie("_fbp")
  const fbc = getCookie("_fbc") || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined)
  return { fbp, fbc }
}

/** Carrega o script base do Facebook Pixel (uma vez) e inicializa com o pixel id. */
function carregarFbPixel(pixelId: string) {
  if (typeof window === "undefined") return
  const w = window as any
  if (!w.fbq) {
    const n: any = (w.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!w._fbq) w._fbq = n
    n.push = n
    n.loaded = true
    n.version = "2.0"
    n.queue = []
    const t = document.createElement("script")
    t.async = true
    t.src = "https://connect.facebook.net/en_US/fbevents.js"
    const s = document.getElementsByTagName("script")[0]
    s.parentNode?.insertBefore(t, s)
  }
  w.fbq("init", pixelId)
}

/** Detecta a origem da visita: param (r/utm_source), click ids (fbclid/gclid) ou referrer. */
function detectarOrigem(): string {
  if (typeof window === "undefined") return "Direto"
  const sp = new URLSearchParams(window.location.search)
  const explicito = sp.get("r") || sp.get("utm_source")
  if (explicito) return rotuloOrigem(explicito)
  if (sp.get("fbclid")) return "Facebook"
  if (sp.get("gclid")) return "Google"
  if (document.referrer) {
    try {
      const host = new URL(document.referrer).hostname.replace(/^www\./, "")
      // Ignora referrer do próprio site (navegação interna)
      if (host && host !== window.location.hostname) return rotuloOrigem(host)
    } catch { /* ignore */ }
  }
  return "Direto"
}

export function PaginaDeVenda({ pagina }: { pagina: PaginaDeVendaDados }) {
  const slug = pagina.slug
  const [origem, setOrigem] = useState("Direto")
  /* O cupom: o do link (`?cupom=`) ou o digitado no pagamento (13/10/2026). */
  const cupom = useCupomDaPagina(slug)

  const [erro, setErro] = useState("")

  // Checkout
  const [showCheckout, setShowCheckout] = useState(false)
  const [formaPagamento, setFormaPagamento] = useState<"PIX" | "CARTAO" | null>(null)
  const [nome, setNome] = useState("")
  const [email, setEmail] = useState("")
  const [telefone, setTelefone] = useState("")
  const [cpf, setCpf] = useState("")
  const [processando, setProcessando] = useState(false)
  const [resultado, setResultado] = useState<CompraResult | null>(null)
  const [vendaId, setVendaId] = useState<string | null>(null)
  // Preco escolhido pelo comprador quando a pagina oferta mais de um. Ja nasce
  // com o primeiro marcado: com um preco so o comprador nao ve escolha
  // nenhuma, e com varios o botao nunca fica sem valor.
  const [precoEscolhidoId, setPrecoEscolhidoId] = useState<string | null>(
    pagina.precos?.[0]?.id ?? null,
  )
  const [parcelasEscolhidas, setParcelasEscolhidas] = useState(1)
  const [copiado, setCopiado] = useState(false)
  const [naoCopiou, setNaoCopiou] = useState(false)
  const [pago, setPago] = useState(false)
  /* Até quando a compra de um preço "todo mês"/"todo ano" pagou o acesso (07/10/2026). */
  const [acessoAte, setAcessoAte] = useState<string | null>(null)
  const [fbEventId, setFbEventId] = useState<string | null>(null)

  /* As ofertas do pagamento marcadas (order bump, 13/10/2026): somam no total. */
  const [ofertasMarcadas, setOfertasMarcadas] = useState<string[]>([])
  /* A oferta que vem com a compra paga (upsell), e o que a pessoa faz com ela. */
  const [ofertaDepois, setOfertaDepois] = useState<OfertaDepois | null>(null)
  const [ofertaDepoisFechada, setOfertaDepoisFechada] = useState(false)
  const [formaDaOferta, setFormaDaOferta] = useState<"PIX" | "CARTAO">("PIX")
  const [parcelasDaOferta, setParcelasDaOferta] = useState(1)
  const [processandoOferta, setProcessandoOferta] = useState(false)
  const [erroDaOferta, setErroDaOferta] = useState("")
  const [resultadoDaOferta, setResultadoDaOferta] = useState<CompraResult | null>(null)
  const [ofertaPaga, setOfertaPaga] = useState(false)
  const [copiadoOferta, setCopiadoOferta] = useState(false)
  const [naoCopiouOferta, setNaoCopiouOferta] = useState(false)

  /* O split (21/10/2026): a venda nasce no Pagar.me, a compra pede o celular de quem
     compra e o cartão é digitado aqui mesmo, sem a página do banco. */
  const split = pagina.gateway === "pagarme"
  /* O celular da oferta depois da compra: a compra de antes de 24/10/2026 podia ter
     ido no cartão sem telefone, e a oferta, no Pix ou no cartão, precisa de um. */
  const [celularDaOferta, setCelularDaOferta] = useState("")
  const [ofertaPedeCelular, setOfertaPedeCelular] = useState(false)

  // Facebook Pixel, guards pra não disparar em duplicidade (StrictMode / re-render)
  const pixelInitRef = useRef(false)
  const purchaseFiredRef = useRef(false)
  const purchaseDaOfertaRef = useRef(false)
  /* Trava do toque duplo: o estado "processando" só chega ao botão no próximo
     desenho, e o segundo toque de um clique duplo vem antes dele. Sem isto, dois
     toques geravam duas cobranças. */
  const comprandoRef = useRef(false)
  const aceitandoOfertaRef = useRef(false)

  const cpfLimpo = cpf.replace(/\D/g, "")
  const cpfValido = cpfLimpo.length === 11 && validarCpf(cpfLimpo)
  /* No split a compra pede o celular de quem compra, no Pix e no cartão: o Pagar.me
     recusa os dois sem telefone (o cartão, medido em 24/10/2026), e a API para a
     compra antes de ela nascer (o 409 FALTA_CELULAR). */
  const faltaCelular = split && !celularDoPix(telefone)
  /* O nome de quem compra vira a conta de aluno: com emoji ou sinal solto, a compra para aqui. */
  const erroDoNome = problemaNoNome(nome)

  useEffect(() => {
    const og = detectarOrigem()
    setOrigem(og)

    // Registra a visualização (VIEW) com a origem detectada
    fetch(`${BASE_URL}/produto/vendas/${slug}/acesso`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ origem: og }),
    }).catch(() => {})
  }, [slug])

  // Registra um evento de interação (clique no checkout), fire-and-forget
  const registrarEvento = (tipo: string) => {
    fetch(`${BASE_URL}/produto/vendas/${slug}/evento`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tipo, origem }),
    }).catch(() => {})
  }

  // Captura de lead (formulário do quiz ou bloco formulário) → lista de leads do personal
  const handleSubmitLead = async (data: { nome: string; email: string; telefone: string }) => {
    if (!pagina?.personal_apelido) return
    await leadService.registrarLead({
      personal_apelido: pagina.personal_apelido,
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      origem,
    })
  }

  // Tracking de etapas do quiz, fire-and-forget
  const registrarQuizEvento = (ev: { quizId: string; etapa: number; total: number; tipo: string }) => {
    fetch(`${BASE_URL}/produto/vendas/${slug}/quiz-evento`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quiz_id: ev.quizId, etapa: ev.etapa, total_etapas: ev.total, tipo: ev.tipo, origem }),
    }).catch(() => {})
  }

  /* A oferta "depois da compra" paga numa venda própria, que nasce quando a pessoa
     toca em "Quero". */
  const vendaDaOferta = resultadoDaOferta?.venda_id ?? null

  /* O cartão do split, um para a compra e outro para a oferta depois dela: cada um
     vai para a venda dele (`/produto/vendas/:vendaId/cartao`), e o token de um não
     serve ao outro. Moram antes das perguntas de situação, que avisam a eles quando
     o antifraude recusa depois da análise. */
  const cartaoDaCompra = usePagarNoCartao({
    enviar: (corpo) => {
      if (!vendaId) throw new Error("Toque em Pagar de novo para continuar.")
      return mandarParaApi<RespostaDoCartao>(`/produto/vendas/${vendaId}/cartao`, corpo)
    },
    aoPagar: () => {
      if (vendaId) void conferirVenda(vendaId)
    },
  })
  const cartaoDaOferta = usePagarNoCartao({
    enviar: (corpo) => {
      if (!vendaDaOferta) throw new Error("Toque em Quero de novo para continuar.")
      return mandarParaApi<RespostaDoCartao>(`/produto/vendas/${vendaDaOferta}/cartao`, corpo)
    },
    aoPagar: () => {
      if (vendaDaOferta) void conferirOferta(vendaDaOferta)
    },
  })
  const { recusadoDepois: compraRecusadaDepois } = cartaoDaCompra
  const { recusadoDepois: ofertaRecusadaDepois } = cartaoDaOferta

  /**
   * Pergunta a situação da compra uma vez. Paga: o acesso até quando, e a oferta
   * "depois da compra", que vem junto do pago quando o produto tem uma e ela ainda
   * vale para quem comprou (já marcada na forma da compra).
   *
   * É a mesma pergunta da espera de cinco em cinco segundos e do cartão do split
   * que acabou de passar: sem ela no cartão, a oferta só chegaria na próxima volta.
   * Com o cartão em análise, `cartao_recusado` diz que o antifraude recusou, e o
   * formulário do cartão volta com a recusa escrita.
   */
  const conferirVenda = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const data = await lerDaApi<{
          status: string
          acesso_ate?: string | null
          oferta_depois?: OfertaDepois | null
          cartao_recusado?: boolean
        }>(`/produto/vendas/status/${id}`)
        if (data.status !== "PAGO") {
          if (data.cartao_recusado) compraRecusadaDepois()
          return false
        }
        setPago(true)
        setAcessoAte(data.acesso_ate ?? null)
        if (data.oferta_depois) {
          setOfertaDepois(data.oferta_depois)
          setFormaDaOferta(data.oferta_depois.forma_da_compra === "CARTAO" ? "CARTAO" : "PIX")
        }
        return true
      } catch {
        return false
      }
    },
    [compraRecusadaDepois],
  )

  // Polling para detectar pagamento confirmado
  useEffect(() => {
    if (!vendaId || pago) return
    const interval = setInterval(async () => {
      if (await conferirVenda(vendaId)) clearInterval(interval)
    }, 5000)
    return () => clearInterval(interval)
  }, [vendaId, pago, conferirVenda])

  // A oferta "depois da compra": espera o pagamento dela do mesmo jeito.
  const conferirOferta = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const data = await lerDaApi<{ status: string; cartao_recusado?: boolean }>(`/produto/vendas/status/${id}`)
        if (data.status !== "PAGO") {
          if (data.cartao_recusado) ofertaRecusadaDepois()
          return false
        }
        setOfertaPaga(true)
        return true
      } catch {
        return false
      }
    },
    [ofertaRecusadaDepois],
  )
  useEffect(() => {
    if (!vendaDaOferta || ofertaPaga) return
    const interval = setInterval(async () => {
      if (await conferirOferta(vendaDaOferta)) clearInterval(interval)
    }, 5000)
    return () => clearInterval(interval)
  }, [vendaDaOferta, ofertaPaga, conferirOferta])

  /* O cartão do split escolhido sem a chave pública no ambiente: só o aviso. */
  const cartaoIndisponivel = split && formaPagamento === "CARTAO" && cartaoDaCompra.indisponivel
  /* A oferta depois da compra pede o celular, no Pix e no cartão, quando a compra não
     deixou um que sirva, ou quando a API disse que faltou. */
  const ofertaPrecisaDeCelular = split && (ofertaPedeCelular || !celularDoPix(telefone))

  // O Purchase da oferta paga, no pixel desta página, com o event_id da venda dela
  // (a CAPI manda o mesmo, no mesmo pixel, e o Meta conta uma venda só).
  useEffect(() => {
    if (!ofertaPaga || purchaseDaOfertaRef.current || !resultadoDaOferta) return
    if (!pagina?.fb_pixel_id || !pagina.fb_pixel_purchase) return
    purchaseDaOfertaRef.current = true
    ;(window as any).fbq?.(
      "trackSingle",
      pagina.fb_pixel_id,
      "Purchase",
      { value: resultadoDaOferta.valor, currency: "BRL" },
      resultadoDaOferta.fb_event_id ? { eventID: resultadoDaOferta.fb_event_id } : undefined,
    )
  }, [ofertaPaga, pagina, resultadoDaOferta])

  // Facebook Pixel: inicializa + PageView quando a página carrega (se configurado)
  useEffect(() => {
    if (!pagina?.fb_pixel_id || pixelInitRef.current) return
    pixelInitRef.current = true
    carregarFbPixel(pagina.fb_pixel_id)
    if (pagina.fb_pixel_pageview) {
      // trackSingle (não track): envia SÓ para o pixel do personal. Se o usuário
      // chegou aqui navegando a partir de uma página do WellTrainer, o nosso
      // Meta Pixel pode já estar no mesmo window.fbq, trackSingle impede o
      // evento do personal de vazar para a nossa conta.
      ;(window as any).fbq?.("trackSingle", pagina.fb_pixel_id, "PageView")
    }
  }, [pagina])

  // Facebook Pixel: dispara Purchase na confirmação do pagamento (se configurado).
  // Usa o valor real da venda (resultado.valor), com fallback no preço da página.
  useEffect(() => {
    if (!pago || purchaseFiredRef.current) return
    if (!pagina?.fb_pixel_id || !pagina.fb_pixel_purchase) return
    purchaseFiredRef.current = true
    const valor = resultado?.valor ?? pagina.preco_valor ?? 0
    // trackSingle → só o pixel do personal (não vaza para o nosso, se presente).
    // eventID compartilhado com a CAPI server-side → o Meta deduplica (conta 1 venda só)
    ;(window as any).fbq?.(
      "trackSingle",
      pagina.fb_pixel_id,
      "Purchase",
      { value: valor, currency: "BRL" },
      fbEventId ? { eventID: fbEventId } : undefined,
    )
  }, [pago, pagina, resultado, fbEventId])

  const handleComprar = async () => {
    if (!formaPagamento || !nome.trim() || erroDoNome || !email.trim() || !cpfValido) return
    if (faltaCelular) return
    if (comprandoRef.current) return
    comprandoRef.current = true
    setProcessando(true)
    setErro("")
    cartaoDaCompra.recomecar()
    try {
      const { fbp, fbc } = coletarFbParams()
      const compraData = await mandarParaApi<CompraResult>(`/produto/vendas/${slug}/comprar`, {
          nome: nome.trim(),
          email: email.trim(),
          telefone: telefone.trim() || undefined,
          cpf: cpfLimpo,
          forma_pagamento: formaPagamento,
          preco_id: precoEscolhidoId || undefined,
          parcelas: formaPagamento === "CARTAO" ? parcelasValidas : 1,
          /* Só as que ainda estão na página: a marcada continua no estado se a
             página recarregar os dados, e a API ignora a que não reconhece. */
          ofertas: ofertasMarcadas.filter((id) => ofertasJunto.some((o) => o.id === id)),
          origem,
          fbp,
          fbc,
          /* O cupom em uso. Com o desconto na tela, a API para a compra se ele
             deixou de valer, em vez de cobrar o preço cheio calada. */
          codigo: cupom.codigoParaCompra ?? undefined,
          desconto_esperado: precoComCupom != null,
      })
      setResultado(compraData)
      setVendaId(compraData.venda_id ?? null)
      setFbEventId(compraData.fb_event_id || null)
    } catch (err) {
      if (err instanceof ErroDaPaginaDePagar && err.codigo === "CUPOM_NAO_VALE") cupom.naoVale(err.message)
      /* Sem internet, ou com a API fora, o navegador joga "Failed to fetch", em
         inglês, e era isso que aparecia no checkout: a frase é nossa. O "falta o
         celular" do split vem escrito pela API, e o campo está logo acima. */
      setErro(fraseDaFalha(err))
    } finally {
      comprandoRef.current = false
      setProcessando(false)
    }
  }

  /* Aceita a oferta "depois da compra": a cobrança sai no nome de quem acabou de
     pagar, sem digitar nada. O segundo toque devolve a mesma cobrança (a API
     reaproveita a que ainda espera pagamento na mesma forma). */
  const aceitarOfertaDepois = async () => {
    if (!ofertaDepois || !vendaId || aceitandoOfertaRef.current) return
    /* O celular da oferta: o que a pessoa digitou agora, ou o da compra. */
    const celularParaOferta = celularDoPix(celularDaOferta)
      ? celularDaOferta
      : celularDoPix(telefone)
        ? telefone
        : null
    if (ofertaPrecisaDeCelular && !celularParaOferta) return
    aceitandoOfertaRef.current = true
    setProcessandoOferta(true)
    setErroDaOferta("")
    cartaoDaOferta.recomecar()
    try {
      const data = await mandarParaApi<CompraResult>(`/produto/vendas/${vendaId}/oferta-depois`, {
        oferta_id: ofertaDepois.id,
        forma_pagamento: formaDaOferta,
        parcelas: formaDaOferta === "CARTAO" ? parcelasDaOfertaValidas : 1,
        ...(split && celularParaOferta ? { celular: celularParaOferta } : {}),
      })
      setResultadoDaOferta(data)
      /* O cartão da oferta já vem com o da compra, menos o código de segurança:
         quem acabou de pagar digita só ele. */
      if (data.gateway === "pagarme" && data.forma === "CARTAO") {
        cartaoDaOferta.preencher({ cartao: cartaoDaCompra.cartao, endereco: cartaoDaCompra.endereco })
      }
    } catch (err) {
      /* A oferta sem celular: a pergunta aparece na caixa da oferta. */
      if (err instanceof ErroDaPaginaDePagar && err.faltaCelular) {
        setOfertaPedeCelular(true)
        return
      }
      setErroDaOferta(
        err instanceof ErroDaPaginaDePagar
          ? err.message
          : err instanceof TypeError
            ? "Não deu para falar com o servidor. Confira a internet e tente de novo."
            : "Não deu para preparar o pagamento agora. Tente de novo.",
      )
    } finally {
      aceitandoOfertaRef.current = false
      setProcessandoOferta(false)
    }
  }

  /* Já na ordem: o servidor ordena uma vez (`lib/seo/pagina-de-venda.ts`). */
  const blocos = pagina.blocos
  const personalNome = `${pagina.personal_nome} ${pagina.personal_sobrenome}`.trim()
  /* A foto do personal na página no ar.

     Era `foto_url: null` fixo: quem ligava "mostrar sua foto de perfil" no bloco
     de topo via a foto no editor e publicava uma página sem ela. A URL sai do
     CDN pelo id do usuário, e só quando a foto existe (`foto_atualizada_em`):
     pedir um arquivo que nunca foi enviado desenha imagem quebrada na página de
     vendas de alguém. */
  const personalInfo = {
    nome: pagina.personal_nome,
    apelido: pagina.personal_apelido,
    foto_url:
      pagina.personal_usuario_id && pagina.personal_foto_atualizada_em
        ? fotoPerfil(pagina.personal_usuario_id, pagina.personal_foto_atualizada_em)
        : null,
  }
  const tema = parseTema(pagina.tema)
  const radius = radiusValue(tema.radius)
  const btnRadius = radiusValue(tema.radius === "full" ? "full" : tema.radius)

  const primaryAccent = tema.primary === tema.background ? tema.foreground : tema.primary

  // Precos ofertados: usa a lista nova e cai no preco escalar (pagina antiga).
  const precosOfertados: PrecoOfertado[] =
    pagina.precos && pagina.precos.length > 0
      ? pagina.precos
      : pagina.preco_id && pagina.preco_valor != null
        ? [{
            id: pagina.preco_id,
            nome: pagina.preco_nome || "Acesso",
            valor: pagina.preco_valor,
            tipo: pagina.preco_tipo,
            periodo: pagina.preco_periodo,
          }]
        : []
  const precoAtual =
    precosOfertados.find((p) => p.id === precoEscolhidoId) ?? precosOfertados[0] ?? null

  /* As ofertas do pagamento (order bump): as marcadas somam no total, e é o total
     que o banco cobra, parcela e corta pelo piso de R$ 5 por parcela. */
  const ofertasJunto = pagina.ofertas_junto ?? []
  const valorDasOfertas = ofertasJunto
    .filter((o) => ofertasMarcadas.includes(o.id))
    .reduce((soma, o) => soma + o.valor, 0)
  /* O cupom tira só do preço do produto: o que foi junto fica no preço dele. */
  const precoComCupom = cupom.precoCom(precoAtual?.id)
  const precoDoProduto = precoComCupom ?? precoAtual?.valor ?? 0
  const totalAVista = precoAtual ? Math.round((precoDoProduto + valorDasOfertas) * 100) / 100 : 0

  // Parcelas oferecidas: limite do produtor, cortado pelo piso de R$ 5 por
  // parcela (mesma regra da API, que recusa abaixo disso).
  const maxParcelas = precoAtual ? parcelasQueCabem(totalAVista, precoAtual.parcelar_em) : 1
  /* Desmarcar uma oferta pode baixar o máximo: a escolha acompanha, e o seletor
     nunca mostra um número que não está na lista. */
  const parcelasValidas = Math.min(parcelasEscolhidas, maxParcelas)
  const acrescimoPorParcelaExtra =
    precoAtual?.parcelamento_taxa_comprador === false
      ? 0
      : (pagina.parcelamento_acrescimo_percentual ?? 2) / 100
  const valorParcelado = (n: number) => (precoAtual ? valorDaParcela(totalAVista, n, acrescimoPorParcelaExtra) : 0)

  /* A oferta "depois da compra": o parcelamento dela é o dela, sobre o preço dela. */
  const maxParcelasDaOferta = ofertaDepois ? parcelasQueCabem(ofertaDepois.valor, ofertaDepois.parcelar_em) : 1
  const parcelasDaOfertaValidas = Math.min(parcelasDaOferta, maxParcelasDaOferta)
  const acrescimoDaOferta =
    ofertaDepois?.parcelamento_taxa_comprador === false ? 0 : (ofertaDepois?.parcelamento_acrescimo_percentual ?? 2) / 100
  const parcelaDaOferta = (n: number) => (ofertaDepois ? valorDaParcela(ofertaDepois.valor, n, acrescimoDaOferta) : 0)
  // Logo do personal (dono da página). Em tema escuro prefere a versão light, se houver.
  const personalLogo = (tema.preset === "dark" && pagina.personal_logo_url_light)
    ? pagina.personal_logo_url_light
    : pagina.personal_logo_url

  return (
    <div className={`min-h-screen ${fontClass(tema.font_body)}`} style={pageThemeStyle(tema)}>
      {/* Header */}
      <div
        className="border-b px-4 py-3"
        style={{ background: tema.surface, borderColor: tema.border }}
      >
        <div className={`${containerMaxClass(tema.container_max)} mx-auto flex items-center justify-between`}>
          <div className="flex items-center gap-2 min-w-0">
            {personalLogo ? (
              <img src={personalLogo} alt={personalNome} className="h-7 max-w-[170px] object-contain" />
            ) : (
              <span className="font-display font-bold text-sm truncate" style={{ color: tema.foreground }}>
                {pagina.personal_apelido || personalNome}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[0.625rem]" style={{ color: tema.muted }}>
            <Icone nome={ICONE.garantia} tamanho={16} /> Seguro
          </div>
        </div>
      </div>
      {cupom.cupom && cupom.deOndeVeio === "link" && <FaixaDoCupom tema={tema} cupom={cupom.cupom} />}

      {/* Blocos */}
      <div className={`${containerMaxClass(tema.container_max)} mx-auto`}>
        {blocos.map((block) => {
          // Bloco checkout: render do formulário real
          if (block.tipo === "checkout" && precoAtual) {
            return (
              <div key={block.id} className="py-8 px-4" id="checkout">
                <div className="max-w-md mx-auto space-y-5">
                  {/* Card de preço, com escolha quando a pagina oferta mais de um */}
                  <div
                    className="p-6 text-center space-y-3"
                    style={{
                      background: `${tema.primary}14`,
                      border: `2px solid ${tema.primary}40`,
                      borderRadius: radius,
                    }}
                  >
                    <h3 className={`${fontClass(tema.font_heading)} text-xl font-bold`}>{block.props.titulo || "Adquira agora"}</h3>

                    {precosOfertados.length > 1 ? (
                      <div className="space-y-2 text-left">
                        {precosOfertados.map((op) => {
                          const marcado = op.id === precoAtual.id
                          return (
                            <OpcaoDaPagina
                              key={op.id}
                              theme={tema}
                              marcada={marcado}
                              onClick={() => setPrecoEscolhidoId(op.id)}
                            >
                              <span className="flex w-full items-center gap-3">
                                <span className="min-w-0 flex-1">
                                  <span className="block font-semibold">{op.nome}</span>
                                  <span className="block text-[0.8125rem]" style={{ color: tema.muted }}>
                                    {op.tipo === "RECORRENTE" && op.periodo
                                      ? `renova todo ${op.periodo === "MENSAL" ? "mês" : "ano"}`
                                      : "pagamento único"}
                                  </span>
                                </span>
                                <PrecoComCupom tema={tema} cheio={op.valor} comCupom={cupom.precoCom(op.id)} cor={primaryAccent} />
                              </span>
                            </OpcaoDaPagina>
                          )
                        })}
                      </div>
                    ) : (
                      <>
                        <PrecoComCupom tema={tema} cheio={precoAtual.valor} comCupom={precoComCupom} cor={primaryAccent} grande />
                        {precoAtual.tipo === "RECORRENTE" && precoAtual.periodo && (
                          <p className="text-xs" style={{ color: tema.muted }}>/{precoAtual.periodo === "MENSAL" ? "mês" : "ano"}</p>
                        )}
                        {precoAtual.tipo === "UNICO" && (
                          <p className="text-xs" style={{ color: tema.muted }}>pagamento único</p>
                        )}
                      </>
                    )}

                    {/* Quem recebe (Decreto 7.962/2013, 23/10/2026): a mesma frase
                        das páginas de pagar (`quemRecebeEscrito`), dentro do resumo
                        da compra, que é onde quem paga confere de quem é. O nome do
                        personal desta página mora no cabeçalho, longe do preço, e a
                        linha lá em cima entraria na marca dele, e não na compra. */}
                    {pagina.quem_recebe && (
                      <TextoDaPagina theme={tema} papel="micro" alinhamento="center">
                        {quemRecebeEscrito(pagina.quem_recebe)}
                      </TextoDaPagina>
                    )}
                  </div>

                  {erro && <ErroDoPagamento>{erro}</ErroDoPagamento>}

                  {resultado ? (
                    <div className="space-y-4 text-center">
                      {pago ? (
                        <>
                          <div className="py-6 space-y-4">
                            <div
                              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
                              style={{ background: "rgba(34,197,94,0.15)" }}
                            >
                              <Icone nome={ICONE.sucesso} tamanho={40} preenchido className="text-green-500" />
                            </div>
                            <h3 className={`${fontClass(tema.font_heading)} text-xl font-bold`}>Pagamento confirmado!</h3>
                            <p className="text-sm" style={{ color: tema.muted }}>Seu acesso ao produto foi liberado.</p>
                            {/* O que foi junto também: quem marcou procura por ele. */}
                            {(resultado.ofertas?.junto?.length ?? 0) > 0 && (
                              <p className="text-sm" style={{ color: tema.muted }}>
                                {`Também foi liberado: ${resultado.ofertas!.junto.map((j) => j.nome).join(", ")}.`}
                              </p>
                            )}
                            {/* "Todo mês": até quando ficou pago, e como renova. Sem
                                isso, quem comprou não sabe que haverá a próxima. */}
                            {acessoAte && (
                              <p className="text-sm" style={{ color: tema.muted }}>
                                {`O acesso vale até ${acessoAte.split("-").reverse().join("/")}. Antes disso, o app avisa para você renovar.`}
                              </p>
                            )}
                          </div>
                          {/* A oferta "depois da compra" (upsell), quando o produto tem uma. */}
                          {ofertaDepois && !ofertaDepoisFechada && (
                            <OfertaDepoisNaPagina
                              tema={tema}
                              oferta={ofertaDepois}
                              forma={formaDaOferta}
                              aoEscolherForma={setFormaDaOferta}
                              parcelas={parcelasDaOfertaValidas}
                              maxParcelas={maxParcelasDaOferta}
                              aoEscolherParcelas={setParcelasDaOferta}
                              valorDaParcelaEm={parcelaDaOferta}
                              comAcrescimo={acrescimoDaOferta > 0}
                              processando={processandoOferta}
                              erro={erroDaOferta}
                              resultado={resultadoDaOferta}
                              paga={ofertaPaga}
                              copiado={copiadoOferta}
                              naoCopiou={naoCopiouOferta}
                              aoCopiar={async () => {
                                if (resultadoDaOferta?.pix_payload) {
                                  const ok = await copiarTexto(resultadoDaOferta.pix_payload)
                                  setNaoCopiouOferta(!ok)
                                  if (!ok) return
                                  setCopiadoOferta(true)
                                  setTimeout(() => setCopiadoOferta(false), 2000)
                                }
                              }}
                              aoAceitar={aceitarOfertaDepois}
                              aoRecusar={() => setOfertaDepoisFechada(true)}
                              aoTrocarForma={() => {
                                setResultadoDaOferta(null)
                                setErroDaOferta("")
                                cartaoDaOferta.recomecar()
                              }}
                              split={split}
                              cartao={cartaoDaOferta}
                              pedeCelular={ofertaPrecisaDeCelular}
                              celular={celularDaOferta}
                              aoMudarCelular={setCelularDaOferta}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {/* O que entrou na conta, quando foi mais que o produto: o
                              Pix e a página do cartão mostram só o total, e sem isto o
                              valor parece errado. */}
                          {((resultado.ofertas?.junto?.length ?? 0) > 0 || !!resultado.cupom) && precoAtual && (
                            <CaixaDaPagina theme={tema} className="text-left">
                              <ul className="space-y-1 text-[0.9375rem]" style={{ color: tema.foreground }}>
                                <li className="flex justify-between gap-3">
                                  <span className="min-w-0 break-words">{pagina.produto_nome}</span>
                                  <span className="shrink-0 tabular-nums">R$ {formatBRL(precoAtual.valor)}</span>
                                </li>
                                {resultado.cupom && (
                                  <li className="flex justify-between gap-3" style={{ color: tema.muted }}>
                                    <span className="min-w-0 break-words">{`Cupom ${resultado.cupom.codigo}`}</span>
                                    <span className="shrink-0 tabular-nums">- R$ {formatBRL(resultado.cupom.desconto)}</span>
                                  </li>
                                )}
                                {(resultado.ofertas?.junto ?? []).map((j) => (
                                  <li key={j.oferta_id} className="flex justify-between gap-3">
                                    <span className="min-w-0 break-words">{j.nome}</span>
                                    <span className="shrink-0 tabular-nums">R$ {formatBRL(j.valor)}</span>
                                  </li>
                                ))}
                                {(() => {
                                  const somaDasPartes =
                                    precoAtual.valor -
                                    (resultado.cupom?.desconto ?? 0) +
                                    (resultado.ofertas?.junto ?? []).reduce((s, j) => s + j.valor, 0)
                                  const acrescimo = Math.round((resultado.valor - somaDasPartes) * 100) / 100
                                  return acrescimo >= 0.01 ? (
                                    <li className="flex justify-between gap-3" style={{ color: tema.muted }}>
                                      <span>Acréscimo do parcelamento</span>
                                      <span className="shrink-0 tabular-nums">R$ {formatBRL(acrescimo)}</span>
                                    </li>
                                  ) : null
                                })()}
                                <li
                                  className="flex justify-between gap-3 pt-1 font-semibold"
                                  style={{ borderTop: `1px solid ${tema.border}` }}
                                >
                                  <span>Total</span>
                                  <span className="shrink-0 tabular-nums">R$ {formatBRL(resultado.valor)}</span>
                                </li>
                              </ul>
                            </CaixaDaPagina>
                          )}
                          {/* A oferta marcada que não entrou: a pessoa já tem o
                              produto, ou ele saiu de venda. Dito antes do pagamento. */}
                          {(resultado.ofertas?.fora_da_compra?.length ?? 0) > 0 && (
                            <CaixaDaPagina theme={tema} className="text-left">
                              <p className="text-[0.9375rem]" style={{ color: tema.foreground }}>
                                {resultado.ofertas!.fora_da_compra
                                  .map((f) =>
                                    fraseDaOfertaFora(
                                      f.nome ?? ofertasJunto.find((o) => o.id === f.oferta_id)?.produto_nome ?? "A oferta",
                                      f.motivo,
                                    ),
                                  )
                                  .join(" ")}
                              </p>
                            </CaixaDaPagina>
                          )}
                          {resultado.pix_qrcode_base64 || resultado.pix_payload ? (
                            <PixDaPagina
                              tema={tema}
                              resultado={resultado}
                              rotuloAcessivel="QR code do Pix para pagar a compra"
                              copiado={copiado}
                              naoCopiou={naoCopiou}
                              aoCopiar={async () => {
                                if (resultado.pix_payload) {
                                  /* "Copiado" só com o código copiado (27/10/2026): sem
                                     cópia, ele aparece escrito embaixo, para copiar à mão. */
                                  const ok = await copiarTexto(resultado.pix_payload)
                                  setNaoCopiou(!ok)
                                  if (!ok) return
                                  setCopiado(true)
                                  setTimeout(() => setCopiado(false), 2000)
                                }
                              }}
                            />
                          ) : resultado.gateway === "pagarme" && resultado.forma === "CARTAO" ? (
                            /* O split: o cartão é digitado aqui, e vai como token para a
                               venda que acabou de nascer. */
                            <CartaoDoSplitNaPagina
                              tema={tema}
                              cartao={cartaoDaCompra}
                              idBase="checkout-cartao"
                              rotuloDoBotao={
                                (resultado.parcelas ?? 1) > 1 && resultado.valor_parcela
                                  ? `Pagar ${resultado.parcelas}x de R$ ${formatBRL(resultado.valor_parcela)} no cartão`
                                  : `Pagar R$ ${formatBRL(resultado.valor)} no cartão`
                              }
                              aoPagarNoPix={() => {
                                /* A venda é do cartão: o Pix é outra compra, com os mesmos dados. */
                                cartaoDaCompra.recomecar()
                                setResultado(null)
                                setVendaId(null)
                                setFbEventId(null)
                                setFormaPagamento("PIX")
                              }}
                            />
                          ) : resultado.invoice_url ? (
                            <BotaoDaPagina theme={tema} onClick={() => window.open(resultado.invoice_url!, "_blank")}>
                              <Icone nome={ICONE.financeiro} tamanho={20} /> Pagar com cartão
                            </BotaoDaPagina>
                          ) : null}
                          <p className="text-xs" style={{ color: tema.muted }}>Após o pagamento, você receberá acesso automaticamente.</p>
                        </>
                      )}
                    </div>
                  ) : !showCheckout ? (
                    <BotaoDaPagina
                      theme={tema}
                      tamanho="grande"
                      onClick={() => { setShowCheckout(true); registrarEvento("CHECKOUT_CLICK") }}
                    >
                      {block.props.texto_botao || "Comprar"}
                    </BotaoDaPagina>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        {/* Escolher como pagar é a mesma peça das respostas do
                            quiz: linha com marca à esquerda, alvo de 48px e foco
                            visível. Antes eram dois cartões sem foco, e o leitor
                            de tela não dizia qual estava escolhido. */}
                        <OpcaoDaPagina
                          theme={tema}
                          marcada={formaPagamento === "PIX"}
                          onClick={() => setFormaPagamento("PIX")}
                        >
                          <span className="inline-flex items-center gap-2">
                            <MarcaPix tamanho={20} /> Pix
                          </span>
                        </OpcaoDaPagina>
                        <OpcaoDaPagina
                          theme={tema}
                          marcada={formaPagamento === "CARTAO"}
                          onClick={() => setFormaPagamento("CARTAO")}
                        >
                          <span className="inline-flex items-center gap-2">
                            <Icone nome={ICONE.financeiro} tamanho={20} /> Cartão
                          </span>
                        </OpcaoDaPagina>
                      </div>
                      {/* O cartão do split sem a chave pública não tem como virar
                          token: o aviso aparece na escolha, antes de a compra nascer,
                          e não depois de a pessoa preencher tudo. */}
                      {cartaoIndisponivel && (
                        <CartaoDaPagina
                          theme={tema}
                          cartao={cartaoDaCompra.cartao}
                          aoMudarCartao={cartaoDaCompra.setCartao}
                          endereco={cartaoDaCompra.endereco}
                          aoMudarEndereco={cartaoDaCompra.setEndereco}
                          indisponivel
                        />
                      )}
                      {formaPagamento === "CARTAO" && maxParcelas > 1 && !cartaoIndisponivel && (
                        <SeletorDeParcelas
                          tema={tema}
                          id="checkout-parcelas"
                          valor={parcelasValidas}
                          maximo={maxParcelas}
                          aoEscolher={setParcelasEscolhidas}
                          aVista={totalAVista}
                          parcela={valorParcelado}
                          comAcrescimo={acrescimoPorParcelaExtra > 0}
                        />
                      )}
                      {formaPagamento && !cartaoIndisponivel && (
                        <div className="space-y-3">
                          {/* Os campos do pagamento usam a mesma peça dos blocos:
                              48px de alvo, 16px de texto (senão o iPhone dá zoom
                              e a página salta no meio do checkout) e foco visível
                              na cor do tema. */}
                          <div>
                            <CampoDaPagina
                              theme={tema}
                              id="checkout-nome"
                              rotulo="Nome completo"
                              placeholder="Seu nome"
                              valor={nome}
                              onChange={setNome}
                              obrigatorio
                            />
                            {erroDoNome && (
                              <p className="mt-1 text-[0.8125rem]" style={{ color: "#DE2828" }}>
                                {erroDoNome}
                              </p>
                            )}
                          </div>
                          <CampoDaPagina
                            theme={tema}
                            id="checkout-email"
                            rotulo="E-mail"
                            tipo="email"
                            teclado="email"
                            placeholder="seu@email.com"
                            valor={email}
                            onChange={setEmail}
                            obrigatorio
                          />
                          {/* No split a compra pede o celular de quem compra, no Pix e
                              no cartão: o Pagar.me recusa os dois sem telefone. */}
                          <CampoDaPagina
                            theme={tema}
                            id="checkout-telefone"
                            rotulo={split ? "Celular" : "Telefone (opcional)"}
                            tipo="tel"
                            teclado="tel"
                            autoComplete="tel-national"
                            placeholder="(00) 00000-0000"
                            valor={telefone}
                            onChange={setTelefone}
                            obrigatorio={split}
                            ajuda={split ? "O banco pede o seu celular, com DDD." : undefined}
                          />
                          <div>
                            <CampoDaPagina
                              theme={tema}
                              id="checkout-cpf"
                              rotulo="CPF"
                              teclado="numeric"
                              placeholder="000.000.000-00"
                              valor={cpf}
                              onChange={(v) => setCpf(mascaraCpf(v))}
                              obrigatorio
                            />
                            {cpfLimpo.length === 11 && !cpfValido && (
                              <p className="mt-1 text-[0.8125rem]" style={{ color: "#DE2828" }}>
                                Este CPF não é válido. Confira os números.
                              </p>
                            )}
                          </div>
                          {/* O cupom (13/10/2026): o do link (`?cupom=`) já vem
                              aplicado, e o digitado vale igual. Fica antes das
                              ofertas, que precisam estar coladas no botão. */}
                          <CampoDoCupom
                            tema={tema}
                            cupom={cupom.cupom}
                            aviso={cupom.aviso}
                            precoNaoCabe={!!cupom.cupom?.desconto && precoComCupom == null}
                            aoAplicar={cupom.aplicar}
                          />
                          {/* As ofertas do pagamento (order bump, 13/10/2026): logo
                              acima do botão, onde quem compra decide. Marcar soma no
                              total do botão na hora. */}
                          {ofertasJunto.length > 0 && (
                            <div className="space-y-2 pt-1" role="group" aria-label="Ofertas para levar junto">
                              {ofertasJunto.map((oferta) => (
                                <OfertaDaPagina
                                  key={oferta.id}
                                  theme={tema}
                                  marcada={ofertasMarcadas.includes(oferta.id)}
                                  onClick={() =>
                                    setOfertasMarcadas((marcadas) =>
                                      marcadas.includes(oferta.id)
                                        ? marcadas.filter((id) => id !== oferta.id)
                                        : [...marcadas, oferta.id],
                                    )
                                  }
                                  titulo={oferta.titulo}
                                  texto={oferta.texto ?? oferta.produto_descricao}
                                  imagem={oferta.produto_thumbnail}
                                  valor={oferta.valor}
                                  valorDe={oferta.valor_de}
                                  fonteTitulo={fontClass(tema.font_heading)}
                                />
                              ))}
                            </div>
                          )}
                          <BotaoDaPagina
                            theme={tema}
                            tamanho="grande"
                            desativado={!nome.trim() || !!erroDoNome || !email.trim() || !cpfValido || faltaCelular}
                            carregando={processando}
                            onClick={handleComprar}
                          >
                            {formaPagamento === "PIX" ? <MarcaPix tamanho={20} /> : <Icone nome={ICONE.financeiro} tamanho={20} />}
                            {/* No cartão do split este toque não paga: ele abre o campo
                                do cartão, com o total que a compra fechou. O rótulo diz
                                isso, em vez de prometer o pagamento. */}
                            {split && formaPagamento === "CARTAO"
                              ? "Continuar para o cartão"
                              : formaPagamento === "CARTAO" && parcelasValidas > 1
                                ? `Pagar ${parcelasValidas}x de R$ ${formatBRL(valorParcelado(parcelasValidas))}`
                                : `Pagar R$ ${formatBRL(totalAVista)}`}
                          </BotaoDaPagina>
                          {/* O preço "todo mês"/"todo ano" cobra de novo (07/10/2026), e
                              isso fica escrito colado no botão, antes do pagamento. */}
                          {precoAtual.tipo === "RECORRENTE" && precoAtual.periodo && (
                            <p className="text-center text-[0.8125rem]" style={{ color: tema.muted }}>
                              {(precoAtual.periodo === "ANUAL"
                                ? "Você paga o primeiro ano agora. A renovação é paga pelo app, com aviso antes de vencer, e dá para cancelar quando quiser."
                                : "Você paga o primeiro mês agora. Os próximos são pagos pelo app, com aviso antes de vencer, e dá para cancelar quando quiser.") +
                                (valorDasOfertas > 0 ? " As ofertas marcadas são pagas uma vez só." : "") +
                                (precoComCupom != null
                                  ? cupom.cupom?.desconto?.nas_renovacoes
                                    ? " O desconto do cupom vale também nas renovações."
                                    : ` O desconto do cupom vale só agora: as renovações saem a R$ ${formatBRL(precoAtual.valor)}.`
                                  : "")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          }

          // Bloco de checkout sem preco ofertado: nao renderiza nada. Antes caia no
          // BlockPreview abaixo, que e o preview do EDITOR, o comprador via a copy
          // interna "Preco definido na configuracao" e um botao morto.
          if (block.tipo === "checkout") return null

          return <BlockPreview key={block.id} block={block} personalInfo={personalInfo} theme={tema} isLive onQuizEvent={registrarQuizEvento} onSubmitLead={handleSubmitLead} />
        })}
      </div>

      {/* Footer */}
      <div className="text-center py-6 border-t" style={{ borderColor: tema.border }}>
        <AssinaturaDaPagina theme={tema} pagina="produto" antes="Processado com segurança via" />
      </div>
    </div>
  )
}



/** O seletor de parcelas do cartão, com o valor de cada uma e o total quando há acréscimo. */
function SeletorDeParcelas({
  tema,
  id,
  valor,
  maximo,
  aoEscolher,
  aVista,
  parcela,
  comAcrescimo,
}: {
  tema: PageTheme
  id: string
  valor: number
  maximo: number
  aoEscolher: (n: number) => void
  aVista: number
  parcela: (n: number) => number
  comAcrescimo: boolean
}) {
  return (
    <div className="space-y-1 text-left">
      <Label htmlFor={id} className="text-xs" style={{ color: tema.muted }}>
        Parcelamento
      </Label>
      <select
        id={id}
        value={valor}
        onChange={(e) => aoEscolher(Number(e.target.value))}
        className="w-full h-11 px-3 text-sm"
        style={{
          background: tema.surface,
          color: tema.foreground,
          border: `1px solid ${tema.border}`,
          borderRadius: radiusValue("sm"),
          textTransform: "none",
        }}
      >
        {Array.from({ length: maximo }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n === 1
              ? `À vista. R$ ${formatBRL(aVista)}`
              : `${n}x de R$ ${formatBRL(parcela(n))}${comAcrescimo ? ` (total R$ ${formatBRL(parcela(n) * n)})` : " sem juros"}`}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * O Pix na página de vendas: o QR (a peça do sistema, com a base branca fixa), o
 * copia e cola e, no split, até quando o código vale. O rótulo do QR sai com a fonte
 * e a cor do personal: a página é dele.
 */
function PixDaPagina({
  tema,
  resultado,
  rotuloAcessivel,
  copiado,
  naoCopiou,
  aoCopiar,
}: {
  tema: PageTheme
  resultado: CompraResult
  rotuloAcessivel: string
  copiado: boolean
  /** O navegador recusou copiar: o código aparece escrito, para copiar à mão. */
  naoCopiou: boolean
  aoCopiar: () => void
}) {
  const validade = validadeDoPixEscrita(resultado.vence_em)
  /* No computador o QR é lido pela câmera do celular e sai maior; no celular a
     pessoa copia o código, e o QR menor deixa o botão acima da dobra. */
  const mobile = useIsMobile()
  return (
    <div className="space-y-3">
      <CodigoQr
        plataforma={mobile ? "mobile" : "desktop"}
        imagemBase64={resultado.pix_qrcode_base64}
        codigo={resultado.pix_payload}
        rotuloAcessivel={rotuloAcessivel}
        rotulo={
          <TextoDaPagina theme={tema} papel="apoio" alinhamento="center">
            Escaneie no app do seu banco
          </TextoDaPagina>
        }
      />
      <BotaoDaPagina theme={tema} onClick={aoCopiar} desativado={!resultado.pix_payload}>
        <Icone nome={copiado ? ICONE.sucesso : ICONE.copiar} tamanho={20} />
        {copiado ? "Código copiado" : "Copiar o código do Pix"}
      </BotaoDaPagina>
      {naoCopiou && resultado.pix_payload && <CodigoDoPixAMao tema={tema} codigo={resultado.pix_payload} />}
      {validade && (
        <TextoDaPagina theme={tema} papel="micro" alinhamento="center">
          {validade}
        </TextoDaPagina>
      )}
    </div>
  )
}

/** O erro do pagamento, na caixa vermelha de sempre da página (o mesmo vermelho do CPF errado). */
function ErroDoPagamento({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="p-3 text-center"
      style={{
        background: "rgba(239,68,68,0.1)",
        border: "1px solid rgba(239,68,68,0.3)",
        borderRadius: radiusValue("sm"),
      }}
    >
      <p className="text-red-500 text-sm">{children}</p>
    </div>
  )
}

/**
 * O cartão do split na página de vendas (21/10/2026): os campos do cartão e da
 * fatura, a recusa escrita, o botão com o valor e a frase de quem processa.
 *
 * Recusado, o formulário continua com o que foi digitado (menos o código de
 * segurança) e dá para tentar outro cartão ou ir para o Pix. Em análise no
 * antifraude, o formulário sai e fica a espera escrita: digitar de novo ali
 * pagaria duas vezes.
 */
function CartaoDoSplitNaPagina({
  tema,
  cartao,
  idBase,
  rotuloDoBotao,
  aoPagarNoPix,
}: {
  tema: PageTheme
  cartao: PagamentoNoCartao
  idBase: string
  rotuloDoBotao: string
  /** Trocar para o Pix: a venda do cartão não serve, e a compra sai de novo no Pix. */
  aoPagarNoPix?: () => void
}) {
  /* O banco aprovou: a confirmação chega na próxima pergunta à API, em segundos.
     Até lá a tela diz que passou, em vez de mostrar o formulário de novo. */
  if (cartao.pago) {
    return (
      <CaixaDaPagina theme={tema} className="space-y-1 text-left">
        <TextoDaPagina theme={tema} papel="corpo" className="font-semibold">
          Pagamento aprovado
        </TextoDaPagina>
        <TextoDaPagina theme={tema} papel="apoio">
          Liberando o seu acesso. Esta tela avisa sozinha.
        </TextoDaPagina>
      </CaixaDaPagina>
    )
  }
  if (cartao.aguardando) {
    return (
      <CaixaDaPagina theme={tema} className="space-y-1 text-left">
        <TextoDaPagina theme={tema} papel="corpo" className="font-semibold">
          Pagamento em análise
        </TextoDaPagina>
        <TextoDaPagina theme={tema} papel="apoio">
          {cartao.aguardando}
        </TextoDaPagina>
      </CaixaDaPagina>
    )
  }
  return (
    <div className="space-y-3">
      <CartaoDaPagina
        theme={tema}
        idBase={idBase}
        cartao={cartao.cartao}
        aoMudarCartao={cartao.setCartao}
        endereco={cartao.endereco}
        aoMudarEndereco={cartao.setEndereco}
        erros={cartao.erros}
        desativado={cartao.enviando}
        indisponivel={cartao.indisponivel}
      />
      {!cartao.indisponivel && (
        <>
          {cartao.erro && <ErroDoPagamento>{cartao.erro}</ErroDoPagamento>}
          <BotaoDaPagina
            theme={tema}
            tamanho="grande"
            desativado={Boolean(cartao.impedimento)}
            carregando={cartao.enviando}
            onClick={() => void cartao.pagar()}
          >
            <Icone nome={ICONE.financeiro} tamanho={20} />
            {rotuloDoBotao}
          </BotaoDaPagina>
          {/* O porquê do botão apagado, colado nele. */}
          {cartao.impedimento && (
            <TextoDaPagina theme={tema} papel="micro" alinhamento="center">
              {cartao.impedimento}
            </TextoDaPagina>
          )}
          <GarantiaDoCartaoDaPagina theme={tema} />
        </>
      )}
      {aoPagarNoPix && (cartao.erro || cartao.indisponivel) && (
        <BotaoDaPagina theme={tema} tom="contorno" onClick={aoPagarNoPix}>
          <MarcaPix tamanho={20} /> Pagar no Pix
        </BotaoDaPagina>
      )}
    </div>
  )
}

/**
 * A oferta "depois da compra" (upsell, 13/10/2026), logo abaixo do "Pagamento
 * confirmado".
 *
 * Quem acabou de pagar não digita nada de novo: escolhe Pix ou cartão (já vem
 * marcada a forma da compra) e toca em "Quero". O Pix sai na hora. O cartão, no
 * Asaas, é digitado de novo na página segura do banco; no split (21/10/2026) ele é
 * digitado aqui, já com o cartão da compra preenchido, e só o código de segurança
 * falta. O Pix do split pede o celular quando a compra não deixou um.
 * "Não, obrigado" fecha a oferta e deixa só a confirmação.
 */
function OfertaDepoisNaPagina({
  tema,
  oferta,
  forma,
  aoEscolherForma,
  parcelas,
  maxParcelas,
  aoEscolherParcelas,
  valorDaParcelaEm,
  comAcrescimo,
  processando,
  erro,
  resultado,
  paga,
  copiado,
  naoCopiou,
  aoCopiar,
  aoAceitar,
  aoRecusar,
  aoTrocarForma,
  split,
  cartao,
  pedeCelular,
  celular,
  aoMudarCelular,
}: {
  tema: PageTheme
  oferta: OfertaDepois
  forma: "PIX" | "CARTAO"
  aoEscolherForma: (f: "PIX" | "CARTAO") => void
  parcelas: number
  maxParcelas: number
  aoEscolherParcelas: (n: number) => void
  valorDaParcelaEm: (n: number) => number
  comAcrescimo: boolean
  processando: boolean
  erro: string
  resultado: CompraResult | null
  paga: boolean
  copiado: boolean
  /** O navegador recusou copiar: o código aparece escrito, para copiar à mão. */
  naoCopiou: boolean
  aoCopiar: () => void
  aoAceitar: () => void
  aoRecusar: () => void
  aoTrocarForma: () => void
  /** A oferta nasce no split: o cartão é digitado aqui. */
  split: boolean
  /** O cartão do split da oferta. */
  cartao: PagamentoNoCartao
  /** O split espera o celular de quem paga, no Pix e no cartão. */
  pedeCelular: boolean
  celular: string
  aoMudarCelular: (v: string) => void
}) {
  const destaque = tema.primary === tema.background ? tema.foreground : tema.primary
  const texto = oferta.texto ?? oferta.produto_descricao
  const faltaCelular = pedeCelular && !celularDoPix(celular)

  if (paga) {
    return (
      <CaixaDaPagina theme={tema} destacada folgada className="space-y-2 text-center">
        <Icone nome={ICONE.sucesso} tamanho={32} preenchido className="mx-auto text-green-500" />
        <p className={`${fontClass(tema.font_heading)} text-lg font-bold break-words`}>
          {`Pronto! ${oferta.produto_nome} também é seu.`}
        </p>
        <p className="text-sm" style={{ color: tema.muted }}>
          O acesso já foi liberado, no mesmo e-mail da compra.
        </p>
      </CaixaDaPagina>
    )
  }

  return (
    <CaixaDaPagina theme={tema} destacada folgada className="text-left">
      <p className="text-[0.8125rem] font-medium" style={{ color: tema.muted }}>
        Oferta para quem acabou de comprar
      </p>
      <div className="mt-3 flex items-start gap-3">
        {oferta.produto_thumbnail && (
          <img
            src={oferta.produto_thumbnail}
            alt=""
            aria-hidden
            className="size-16 shrink-0 object-cover"
            style={{ borderRadius: radiusValue(tema.radius === "full" ? "md" : tema.radius) }}
          />
        )}
        <div className="min-w-0 flex-1">
          <h4 className={`${fontClass(tema.font_heading)} text-lg font-bold leading-6 break-words`}>{oferta.titulo}</h4>
          {texto && (
            <p className="mt-1 text-sm break-words" style={{ color: tema.muted }}>
              {texto}
            </p>
          )}
        </div>
      </div>
      <p className="mt-3 flex flex-wrap items-baseline gap-x-2">
        {oferta.valor_de != null && (
          <s className="text-sm" style={{ color: tema.muted }}>
            R$ {formatBRL(oferta.valor_de)}
          </s>
        )}
        <span className="text-2xl font-bold" style={{ color: destaque }}>
          R$ {formatBRL(oferta.valor)}
        </span>
        <span className="text-sm" style={{ color: tema.muted }}>
          pagamento único
        </span>
      </p>

      {resultado ? (
        <div className="mt-4 space-y-3 text-center">
          {resultado.pix_qrcode_base64 || resultado.pix_payload ? (
            <PixDaPagina
              tema={tema}
              resultado={resultado}
              rotuloAcessivel="QR code do Pix da oferta"
              copiado={copiado}
              naoCopiou={naoCopiou}
              aoCopiar={aoCopiar}
            />
          ) : split && resultado.gateway === "pagarme" && resultado.forma === "CARTAO" ? (
            <CartaoDoSplitNaPagina
              tema={tema}
              cartao={cartao}
              idBase="oferta-cartao"
              rotuloDoBotao={
                (resultado.parcelas ?? 1) > 1 && resultado.valor_parcela
                  ? `Pagar ${resultado.parcelas}x de R$ ${formatBRL(resultado.valor_parcela)} no cartão`
                  : `Pagar R$ ${formatBRL(resultado.valor)} no cartão`
              }
            />
          ) : resultado.invoice_url ? (
            <BotaoDaPagina theme={tema} onClick={() => window.open(resultado.invoice_url!, "_blank")}>
              <Icone nome={ICONE.financeiro} tamanho={20} /> Pagar com cartão
            </BotaoDaPagina>
          ) : null}
          <p className="text-xs" style={{ color: tema.muted }}>
            Assim que o pagamento entrar, o acesso é liberado no mesmo e-mail da compra.
          </p>
          {!cartao.aguardando && !cartao.pago && (
            <BotaoDaPagina theme={tema} tom="contorno" onClick={aoTrocarForma}>
              Pagar de outro jeito
            </BotaoDaPagina>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="space-y-2" role="radiogroup" aria-label="Como pagar a oferta">
            <OpcaoDaPagina theme={tema} marcada={forma === "PIX"} onClick={() => aoEscolherForma("PIX")}>
              <span className="inline-flex items-center gap-2">
                <MarcaPix tamanho={20} /> Pix
              </span>
            </OpcaoDaPagina>
            <OpcaoDaPagina theme={tema} marcada={forma === "CARTAO"} onClick={() => aoEscolherForma("CARTAO")}>
              <span className="inline-flex items-center gap-2">
                <Icone nome={ICONE.financeiro} tamanho={20} /> Cartão
              </span>
            </OpcaoDaPagina>
          </div>
          {forma === "CARTAO" && maxParcelas > 1 && (
            <SeletorDeParcelas
              tema={tema}
              id="oferta-parcelas"
              valor={parcelas}
              maximo={maxParcelas}
              aoEscolher={aoEscolherParcelas}
              aVista={oferta.valor}
              parcela={valorDaParcelaEm}
              comAcrescimo={comAcrescimo}
            />
          )}
          {/* A oferta sem celular: a compra não deixou um que sirva, e o Pagar.me
              recusa o Pix e o cartão de quem paga sem telefone. */}
          {pedeCelular && (
            <CampoDaPagina
              theme={tema}
              id="oferta-celular"
              rotulo="Celular"
              tipo="tel"
              teclado="tel"
              autoComplete="tel-national"
              placeholder="(00) 00000-0000"
              valor={celular}
              onChange={aoMudarCelular}
              obrigatorio
              ajuda="O banco pede o seu celular, com DDD."
            />
          )}
          {erro && (
            <p className="text-sm" role="alert" style={{ color: "#DE2828" }}>
              {erro}
            </p>
          )}
          <BotaoDaPagina
            theme={tema}
            tamanho="grande"
            carregando={processando}
            desativado={faltaCelular}
            onClick={aoAceitar}
          >
            {forma === "PIX" ? <MarcaPix tamanho={20} /> : <Icone nome={ICONE.financeiro} tamanho={20} />}
            {forma === "CARTAO" && parcelas > 1
              ? `Quero, pagar ${parcelas}x de R$ ${formatBRL(valorDaParcelaEm(parcelas))}`
              : `Quero, pagar R$ ${formatBRL(oferta.valor)}`}
          </BotaoDaPagina>
          <BotaoDaPagina theme={tema} tom="contorno" onClick={aoRecusar}>
            Não, obrigado
          </BotaoDaPagina>
        </div>
      )}
    </CaixaDaPagina>
  )
}

/** Página de venda que não existe ou não está publicada (`not-found.tsx`). O mesmo aviso de antes. */
export function PaginaDeVendaNaoEncontrada() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: TEMA_DEFAULT.background, color: TEMA_DEFAULT.foreground }}>
      <div className="text-center space-y-3">
        <Icone nome={ICONE.bloqueado} tamanho={40} className="mx-auto opacity-30" />
        <p className="opacity-60">Página não encontrada.</p>
      </div>
    </div>
  )
}

/**
 * O código do Pix escrito, para copiar à mão, quando o navegador recusou copiar
 * (27/10/2026). Só com o QR, quem paga pelo próprio celular não tinha o que colar
 * no app do banco, e o botão dizia "Copiado" assim mesmo.
 */
function CodigoDoPixAMao({ tema, codigo }: { tema: PageTheme; codigo: string }) {
  return (
    <div className="space-y-2 text-left">
      <AvisoDaPagina theme={tema}>Não deu para copiar. Toque e segure no código para copiar à mão.</AvisoDaPagina>
      <CampoDaPagina theme={tema} rotulo="Código do Pix" valor={codigo} onChange={() => {}} somenteLeitura />
    </div>
  )
}
